import {Component, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {DaPartnerService} from "../../services/da-partner.service";
import {DOCUMENT} from "@angular/common";
import {ReplaySubject} from "rxjs";
import {ListResponse} from "../../interface/response/list-response";
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from "rxjs/operators";
import {MatSelectChange} from "@angular/material/select";
import {DobryAgentPartner} from "../../interface/dobry-agent-partner";

@Component({
  selector: 'app-dict-dobry-agent-partners',
  templateUrl: './dict-dobry-agent-partners.component.html',
  styleUrls: ['./dict-dobry-agent-partners.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictDobryAgentPartnersComponent)}
  ]
})
export class DictDobryAgentPartnersComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() partnerItems: DobryAgentPartner[] = null;
  @Output() selectedPartner: EventEmitter<DobryAgentPartner> = new EventEmitter<DobryAgentPartner>();
  @Input() minimalDisplay = false;

  externalControl = true;
  disabled = false;
  items: DobryAgentPartner[] = [];
  value?: string = null;
  partnerFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<DobryAgentPartner>> = new ReplaySubject<ListResponse<DobryAgentPartner>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private dobryAgentPartnerService: DaPartnerService,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
  ) {
    this.partnerFilterCtrl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.partnerItems !== null) {
      this.items = this.partnerItems;
    }

    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((partner: DobryAgentPartner) => {
            if (partner.partnerUuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            this.dobryAgentPartnerService.loadNotCountedList(0, 9, {
              in$uuid: val
            }, this.itemsSubject);
          }
        }
      });

    this.partnerFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (val) {
          this.dobryAgentPartnerService.loadNotCountedList(0, 5, {
            context: val,
          }, this.itemsSubject);
        } else if (this.control.value) {
          this.dobryAgentPartnerService.loadNotCountedList(0, 9, {
            in$uuid: this.control.value
          }, this.itemsSubject);
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<DobryAgentPartner>) => {
        if (this.partnerItems === null) {
          this.items = result.items;
        } else {
          this.items = this.partnerItems;
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((dobryAgentPartner: DobryAgentPartner) => {
      if (dobryAgentPartner.partnerUuid === e.value) {
        this.selectedPartner.emit(dobryAgentPartner);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }
}
